import { CloseButton, Combobox, Input, InputBase, Loader, Pill, PillsInput, Text, useCombobox } from '@mantine/core';
import React from 'react';

export const ElcanoCombo = ({
  data, // format: [{label: 'Label', value: 'Value'}, ...]
  withAsterisk = false,
  clearAction, // boolean or function
  disabled = false,
  loading = false,
  value = [], // if multiselect, value is an array of objects (label, value), else value is a string
  label, // string
  onChange,
  multiselect = false,
  creatable = false,
  onCreate,
  getCreateLabel,
  leftSection,
  placeholder,
  description,
  error,
  classNames,
  ...props
}) => {
  // const el = React.useRef();
  const [search, setSearch] = React.useState('');
  const filteredData = React.useMemo(() => data.filter(item => item.label !== null && item.value !== null), [data]);
  const selectedLabel = React.useMemo(
    () =>
      data.find(
        item =>
          item.value.toLowerCase().replace(/[^a-z0-9]/g, '-') ===
          String(value)
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '-'),
      ),
    [data, value],
  );
  const isExactOptionMatch = React.useMemo(
    () =>
      filteredData.some(item => {
        const string = item?.label || item;
        return string.toLowerCase().replace(/[^a-z0-9]/g, '-') === search.toLowerCase().replace(/[^a-z0-9]/g, '-');
      }),
    [(filteredData, search)],
  );

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      if (!multiselect) {
        combobox.focusTarget();
      }
      setSearch('');
    },

    onDropdownOpen: () => {
      combobox.updateSelectedOptionIndex();
      if (!multiselect) {
        combobox.focusSearchInput();
      }
    },
  });

  // filter by search term
  const searchResultsData = React.useMemo(
    () =>
      filteredData
        // filter by slug
        .filter(item => {
          const string = item?.label || item?.value || item;
          return string
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .includes(
              search
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '')
                .trim(),
            );
        })
        // sort by sort key
        .sort((a, b) => parseFloat(a.order) - parseFloat(b.order))
        // render options
        .map(item => (
          <Combobox.Option value={item?.value || item} key={item?.value || item}>
            {item?.label || item?.value || item}
          </Combobox.Option>
        )),
    [filteredData, search],
  );

  // select the first result on search
  React.useEffect(() => {
    if (search !== '') {
      combobox.selectFirstOption();
    }
  }, [search]);

  const handleOnOptionSelect = val => {
    if (val === '$create' && onCreate) {
      const item = onCreate(search);
      val = item?.value || item;
    }
    if (multiselect) {
      val = [...new Set([...value, val])];
    }
    onChange && onChange(val);
    multiselect && setSearch('');
    !multiselect && combobox.closeDropdown();
  };

  const handleOnRemovePill = val => {
    onChange && onChange(value.filter(v => v !== val));
  };

  return (
    <Combobox
      store={combobox}
      shadow={'sm'}
      disabled={disabled}
      onOptionSubmit={handleOnOptionSelect}
      description={description}
      error={error}
      {...props}
    >
      {!multiselect && (
        <Combobox.Target>
          <InputBase
            leftSection={leftSection}
            withAsterisk={withAsterisk}
            label={label}
            classNames={classNames}
            disabled={disabled}
            component="button"
            type="button"
            pointer
            rightSection={
              loading ? (
                <Loader size={18} />
              ) : value && clearAction !== false && !disabled ? (
                <CloseButton
                  size="sm"
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => (clearAction ? clearAction : onChange(''))}
                  aria-label="Clear value"
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            onClick={() => combobox.toggleDropdown()}
            rightSectionPointerEvents={!value ? 'none' : 'all'}
          >
            <Text truncate>
              {selectedLabel ? selectedLabel.label : <Input.Placeholder>{placeholder || 'Pick one'}</Input.Placeholder>}
            </Text>
          </InputBase>
        </Combobox.Target>
      )}
      {multiselect && (
        <Combobox.DropdownTarget>
          <PillsInput label={label} leftSection={leftSection} withAsterisk={withAsterisk} onClick={() => combobox.openDropdown()}>
            <Pill.Group>
              {value &&
                value.map(item => (
                  <Pill key={item?.value || item} withRemoveButton onRemove={() => handleOnRemovePill(item)}>
                    {item?.label || data.find(pillItem => pillItem.value === item)?.label || item}
                  </Pill>
                ))}

              <Combobox.EventsTarget>
                <PillsInput.Field
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => combobox.closeDropdown()}
                  value={search}
                  placeholder="Search values"
                  onChange={event => {
                    combobox.updateSelectedOptionIndex();
                    setSearch(event.currentTarget.value);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Backspace' && search.length === 0) {
                      event.preventDefault();
                      handleOnRemovePill(value[value.length - 1]);
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>
      )}
      <Combobox.Dropdown>
        {!multiselect && (
          <Combobox.Search value={search} onChange={event => setSearch(event.currentTarget.value)} placeholder="Search..." />
        )}
        <Combobox.Options mah={250} style={{ overflowY: 'auto' }}>
          {searchResultsData.length > 0 && searchResultsData}

          {!isExactOptionMatch && search.trim().length > 0 && creatable && (
            <Combobox.Option value="$create">{getCreateLabel ? getCreateLabel(search) : '+ Create ' + search}</Combobox.Option>
          )}

          {search.trim().length > 0 && searchResultsData.length === 0 && !creatable && <Combobox.Empty>No option</Combobox.Empty>}

          {search.trim().length === 0 && searchResultsData.length === 0 && <Combobox.Empty>No option</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
